import { createCanvas } from '@/utils/file-kit';
import {
    getFontFamilyWithFallback,
    getPlainTextFontByPretext,
    getPlainTextLayoutByPretext,
    measurePlainTextGraphemes,
    type IPlainTextGraphemeMeasurement,
} from '@/utils/text-kit/plain-text-measure';
import { TextWritingType } from '@/version-types';

const FONT_SIZE = 270;
const DRAWING_PADDING = FONT_SIZE;
const HORIZONTAL_LINE_HEIGHT = 1.2;
const HORIZONTAL_LETTER_SPACING = 10;
const VERTICAL_LINE_HEIGHT = 1.05;
const VERTICAL_LETTER_SPACING = 40;

interface IAiTextCanvasOptions {
    text: string;
    fontFamily: string;
    bold: boolean;
    italic: boolean;
    writingMode: TextWritingType;
    color?: string;
    background?: string;
    rowTexts?: number;
    padding?: number;
}

interface IAlphaBounds {
    left: number;
    top: number;
    width: number;
    height: number;
}

const graphemeSegmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' });

type LoadDocumentFontSubset = (
    fontFamily: string,
    chars: string,
    options: { registerTo: 'document' },
) => Promise<string | null>;

export const loadAiTextCanvasFontFamily = async (
    originalFontFamily: string,
    text: string,
    loadSubset: LoadDocumentFontSubset,
) => {
    const subsetFontFamily = originalFontFamily
        ? await loadSubset(originalFontFamily, text, { registerTo: 'document' })
        : null;
    return getFontFamilyWithFallback(originalFontFamily, subsetFontFamily);
};

export const splitAiTextIntoLines = (text: string, rowTexts: number) => {
    const graphemes = Array.from(graphemeSegmenter.segment(text.replace(/\s+/g, '')), ({ segment }) => segment);
    const groupSize = Math.max(1, Math.floor(rowTexts) || 1);
    const lines: string[] = [];
    for (let index = 0; index < graphemes.length; index += groupSize) {
        lines.push(graphemes.slice(index, index + groupSize).join(''));
    }
    return lines;
};

const getAlphaBounds = (canvas: HTMLCanvasElement): IAlphaBounds | null => {
    const ctx = canvas.getContext('2d');
    if (!ctx || !canvas.width || !canvas.height) return null;

    const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    let left = canvas.width;
    let top = canvas.height;
    let right = -1;
    let bottom = -1;

    for (let y = 0; y < canvas.height; y += 1) {
        for (let x = 0; x < canvas.width; x += 1) {
            if (pixels[(y * canvas.width + x) * 4 + 3] === 0) continue;
            left = Math.min(left, x);
            top = Math.min(top, y);
            right = Math.max(right, x);
            bottom = Math.max(bottom, y);
        }
    }

    if (right < left || bottom < top) return null;
    return {
        left,
        top,
        width: right - left + 1,
        height: bottom - top + 1,
    };
};

const createEmptyCanvas = (background: string | undefined, padding: number) => {
    const size = Math.max(1, padding * 2);
    const { canvas, ctx } = createCanvas(size, size);
    if (background) {
        ctx.fillStyle = background;
        ctx.fillRect(0, 0, size, size);
    }
    return canvas;
};

const getGraphemeMeasurements = (
    text: string,
    fontOptions: Pick<IAiTextCanvasOptions, 'fontFamily' | 'bold' | 'italic'>,
    ctx: CanvasRenderingContext2D,
) => {
    const measurements = measurePlainTextGraphemes(text, {
        ...fontOptions,
        fontSize: FONT_SIZE,
    });
    if (measurements) return measurements;

    return Array.from(graphemeSegmenter.segment(text), ({ segment }) => ({
        text: segment,
        width: ctx.measureText(segment).width,
    }));
};

const getLineWidth = (graphemes: IPlainTextGraphemeMeasurement[], letterSpacing: number) =>
    graphemes.reduce((width, grapheme) => width + grapheme.width, 0) +
    Math.max(0, graphemes.length - 1) * letterSpacing;

export const renderAiTextCanvas = (options: IAiTextCanvasOptions) => {
    const padding = Math.max(0, options.padding ?? 0);
    const lines = splitAiTextIntoLines(options.text, options.rowTexts ?? 6);
    if (!lines.length) return createEmptyCanvas(options.background, padding);

    const isHorizontal = options.writingMode === TextWritingType.HORIZONTAL_TB;
    const lineHeightPx = Math.ceil(FONT_SIZE * (isHorizontal ? HORIZONTAL_LINE_HEIGHT : VERTICAL_LINE_HEIGHT));
    const letterSpacing = isHorizontal ? HORIZONTAL_LETTER_SPACING : VERTICAL_LETTER_SPACING;
    const fontOptions = {
        fontFamily: options.fontFamily,
        bold: options.bold,
        italic: options.italic,
    };
    const font = getPlainTextFontByPretext({ ...fontOptions, fontSize: FONT_SIZE });
    const pretextLayout = getPlainTextLayoutByPretext(lines.join('\n'), {
        ...fontOptions,
        fontSize: FONT_SIZE,
        lineHeightPx,
        width: Number.POSITIVE_INFINITY,
        disableAutoWrap: true,
    });
    const layoutLines = pretextLayout.lines.length ? pretextLayout.lines.map(({ text }) => text) : lines;

    const measureCanvas = createCanvas(1, 1);
    measureCanvas.ctx.font = font;
    const measuredLines = layoutLines.map((line) => getGraphemeMeasurements(line, fontOptions, measureCanvas.ctx));
    const contentWidth = isHorizontal
        ? Math.max(...measuredLines.map((line) => getLineWidth(line, letterSpacing)))
        : measuredLines.length * lineHeightPx;
    const contentHeight = isHorizontal
        ? measuredLines.length * lineHeightPx
        : Math.max(
              ...measuredLines.map((line) => line.length * FONT_SIZE + Math.max(0, line.length - 1) * letterSpacing),
          );
    const drawingWidth = Math.max(1, Math.ceil(contentWidth + DRAWING_PADDING * 2));
    const drawingHeight = Math.max(1, Math.ceil(contentHeight + DRAWING_PADDING * 2));
    const { canvas: drawingCanvas, ctx } = createCanvas(drawingWidth, drawingHeight);

    const drawText = (fillStyle: string) => {
        ctx.font = font;
        ctx.fillStyle = fillStyle;
        ctx.textBaseline = 'middle';

        if (isHorizontal) {
            ctx.textAlign = 'left';
            measuredLines.forEach((graphemes, lineIndex) => {
                let x = DRAWING_PADDING;
                const y = DRAWING_PADDING + lineIndex * lineHeightPx + lineHeightPx / 2;
                graphemes.forEach((grapheme, graphemeIndex) => {
                    ctx.fillText(grapheme.text, x, y);
                    x += grapheme.width + (graphemeIndex < graphemes.length - 1 ? letterSpacing : 0);
                });
            });
            return;
        }

        ctx.textAlign = 'center';
        measuredLines.forEach((graphemes, lineIndex) => {
            const columnIndex =
                options.writingMode === TextWritingType.VERTICAL_RL ? measuredLines.length - lineIndex - 1 : lineIndex;
            const x = DRAWING_PADDING + columnIndex * lineHeightPx + lineHeightPx / 2;
            let y = DRAWING_PADDING + FONT_SIZE / 2;
            graphemes.forEach((grapheme, graphemeIndex) => {
                ctx.fillText(grapheme.text, x, y);
                y += FONT_SIZE + (graphemeIndex < graphemes.length - 1 ? letterSpacing : 0);
            });
        });
    };

    drawText('#000');
    const bounds = getAlphaBounds(drawingCanvas);
    if (!bounds) return createEmptyCanvas(options.background, padding);

    ctx.clearRect(0, 0, drawingWidth, drawingHeight);
    drawText(options.color ?? '#000');

    const finalWidth = bounds.width + padding * 2;
    const finalHeight = bounds.height + padding * 2;
    const { canvas, ctx: finalCtx } = createCanvas(finalWidth, finalHeight);
    if (options.background) {
        finalCtx.fillStyle = options.background;
        finalCtx.fillRect(0, 0, finalWidth, finalHeight);
    }
    finalCtx.drawImage(
        drawingCanvas,
        bounds.left,
        bounds.top,
        bounds.width,
        bounds.height,
        padding,
        padding,
        bounds.width,
        bounds.height,
    );
    return canvas;
};
